/* =============================================================================
   The interaction vocabulary
   -----------------------------------------------------------------------------
   Motion values used to be typed into each component as literal seconds —
   Tooltip 0.12, Menu 0.16, Dialog 0.18/0.22, Toast 0.22, PdfPreview 0.18,
   Badge 0.22, Accordion 0.24/0.32, Metric 0.28 — while `tokens.css` publishes
   `--dur-fast: 140ms`, `--dur-base: 220ms`, `--dur-slow: 340ms`. Only one of
   those literals happened to coincide with a token, so a duration change in the
   token file reached exactly one component.

   These helpers read the custom properties off `:root` at first use and cache
   them, so an edit to `--dur-base` or `--ease-overshoot` reaches every call site
   with no second place to update. The `FALLBACK` values are the token file's own
   numbers, used only when `document` is unavailable.

   ONE EASING PER INTENT. Four intents, four curves, all of them tokens:
     out        — an element leaving/entering a transient surface (Tooltip, Menu,
                  Popover, Toast, Select, Metric).
     inOut      — a reversible move (reserved).
     overshoot  — a surface that settles past its resting size, the motion
                  equivalent of the hard-shadow press (Dialog, Fab, Badge,
                  Accordion's chevron).
     spring     — the hover/press settle on a control (Button, IconButton, Fab).
   ============================================================================= */

export type DurationIntent = 'instant' | 'fast' | 'base' | 'slow' | 'slower' | 'reveal';
export type EaseIntent = 'out' | 'inOut' | 'overshoot' | 'spring';

/** `[x1, y1, x2, y2]` — the tuple Motion takes for a cubic bezier. */
export type Bezier = [number, number, number, number];

/** The token file's own values, so a non-browser render is never undefined. */
const FALLBACK_DURATION_MS: Record<DurationIntent, number> = {
  instant: 80,
  fast: 140,
  base: 220,
  slow: 340,
  slower: 540,
  reveal: 720,
};

const FALLBACK_EASING: Record<EaseIntent, Bezier> = {
  out: [0.16, 1, 0.3, 1],
  inOut: [0.65, 0, 0.35, 1],
  overshoot: [0.22, 1.4, 0.36, 1],
  spring: [0.34, 1.4, 0.64, 1],
};

/** Custom property per intent. `inOut` is `--ease-in-out`, not `--ease-inout`. */
const EASE_TOKEN: Record<EaseIntent, string> = {
  out: '--ease-out',
  inOut: '--ease-in-out',
  overshoot: '--ease-overshoot',
  spring: '--ease-spring',
};

function readToken(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value.length > 0 ? value : null;
}

/** `140ms` → 140, `0.22s` → 220. */
function parseMilliseconds(raw: string): number | null {
  const value = Number.parseFloat(raw);
  if (!Number.isFinite(value)) return null;
  return raw.endsWith('ms') ? value : value * 1000;
}

function parseBezier(raw: string): Bezier | null {
  const match = /cubic-bezier\(([^)]+)\)/.exec(raw);
  if (!match?.[1]) return null;
  const parts = match[1].split(',').map((part) => Number.parseFloat(part));
  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part))) return null;
  return [parts[0]!, parts[1]!, parts[2]!, parts[3]!];
}

const durationCache = new Map<DurationIntent, number>();
const easingCache = new Map<EaseIntent, Bezier>();

/** Seconds, ready for Motion's `transition.duration`. */
export function duration(intent: DurationIntent): number {
  const cached = durationCache.get(intent);
  if (cached !== undefined) return cached;

  const raw = readToken(`--dur-${intent}`);
  const milliseconds = raw === null ? null : parseMilliseconds(raw);
  const seconds = (milliseconds ?? FALLBACK_DURATION_MS[intent]) / 1000;
  durationCache.set(intent, seconds);
  return seconds;
}

/** A bezier tuple, ready for Motion's `transition.ease`. */
export function easing(intent: EaseIntent): Bezier {
  const cached = easingCache.get(intent);
  if (cached !== undefined) return cached;

  const raw = readToken(EASE_TOKEN[intent]);
  const bezier = (raw === null ? null : parseBezier(raw)) ?? FALLBACK_EASING[intent];
  easingCache.set(intent, bezier);
  return bezier;
}

/** The per-item delay of an entrance sequence, in seconds (`--stagger-step`). */
export function staggerStep(): number {
  const raw = readToken('--stagger-step');
  const parsed = raw === null ? null : parseMilliseconds(raw);
  return (parsed ?? 60) / 1000;
}

/**
 * A capped stagger: item `index`'s delay, with the cascade stopped after `cap`
 * items so a long list does not take seconds to become readable.
 */
export function stagger(cap: number): (index: number) => number {
  const step = staggerStep();
  return (index) => Math.min(index, cap) * step;
}

/** Dotted access for call sites: `DUR.fast`, `EASE.overshoot`. */
export const DUR = {
  get instant() {
    return duration('instant');
  },
  get fast() {
    return duration('fast');
  },
  get base() {
    return duration('base');
  },
  get slow() {
    return duration('slow');
  },
  get slower() {
    return duration('slower');
  },
  get reveal() {
    return duration('reveal');
  },
} as const;

export const EASE = {
  get out() {
    return easing('out');
  },
  get inOut() {
    return easing('inOut');
  },
  get overshoot() {
    return easing('overshoot');
  },
  get spring() {
    return easing('spring');
  },
} as const;

/* -----------------------------------------------------------------------------
   The press state
   -----------------------------------------------------------------------------
   One treatment for every control the library does NOT raise with a hard shadow.

   The lift-and-drop press belongs to raised surfaces: on hover a Button or a Fab
   lifts away from `--shadow-hard`, on press it drops back onto it. A flat
   control has no shadow to drop onto, so its press is the same idea at one
   notch: a 1px nudge down, the direction the shadow language moves. IconButton
   and the unelevated Fab already pressed this way; this constant is that
   treatment published once, so Tabs, SegmentedControl, Accordion, Stepper, List,
   Badge, AvatarGroup, IconGrid, the table's controls, NotificationsList,
   FormErrorSummary, InputNumber's steppers, PopoverShell and the header's
   controls all press identically instead of not at all.

   It is a state cue, not decoration: it fires only on `:active`, it is a
   transform the global reduced-motion rule settles instantly, and it never
   changes a colour, so it cannot move a contrast ratio.
   -------------------------------------------------------------------------- */
export const PRESS = 'active:translate-y-px';
